import React, { useState, useContext, useEffect } from 'react';
import { Breadcrumb, Button, ButtonGroup } from 'react-bootstrap';
import MarkSheetDisplay from './MarkSheetDisplay';
import Preloader from '../../components/Preloader/Preloader';
import { ProductContext } from '../../ProductProvider';
import { Link } from "react-router-dom";
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useSnackbar } from 'notistack';
import MarkSheetView from '../student/MarkSheetView';
import { Helmet } from 'react-helmet-async';

const config = require('../../config.json');

export default function MarksMainView() {
    const { loggedUser, validateLogin } = useContext(ProductContext);
    const [viewSelected, setViewSelected] = useState("Print");
    const [searchResults, setSearchResults] = useState([]);
    const [profileDetails, setProfileDetails] = useState({
        applicantName: '', fatherName: '', dateOfBirth: '', gender: 'Male', nationality: '',
        religiousStatus: 'Scholastic', parishDiocese: '', congregationProvince: '', areaCode: '+91', mobileNumber: '', emailID: '', placeOfBirth: '',
        birthDistrict: '', birthState: '', localAddress: '', localState: '', localPincode: '', localContact: '', perAddress: '', perState: '', perPincode: '',
        perContactNo: '', courseCategory: 'Bachelor of Theology', degreeDetails: [], languageDetails: [], courseDetails: []
    });
    const [markDetails, setMarkDetails] = useState([]);
    const [rawMarks, setRawMarks] = useState([]);
    const [filterValue, setFilterValue] = useState("");
    const [loaded, setLoaded] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if (loggedUser.isAuthenticated) {
            handleSearch();
            setViewSelected("Print");
        } else {
            validateLogin();
        }
    }, [loggedUser]);

    const handleSearch = async () => {
        try {
            setLoaded(false);
            const { idToken } = (await fetchAuthSession()).tokens ?? {};
            const searchValue = loggedUser.nickname || '23BTH1001';
            // const searchValue = "24BTH1032";

            await axios.get(`${config.api.invokeURL}/dashboard/student/marksheet/view?searchValue=${searchValue}`, {
                headers: {
                    Authorization: idToken,
                    'x-api-key': config.api.key
                }
            }).then((response) => {
                if (response.data && response.data.length > 0) {
                    setSearchResults(response.data);
                    if (response.data[0] && response.data[0][0]) {
                        setProfileDetails(response.data[0][0]);
                    }
                    if (response.data[1]) {
                        setMarkDetails(response.data[1]);
                        setRawMarks(response.data[1]);
                    }
                }
                enqueueSnackbar("Marksheet loaded successfully", { variant: 'success' });
                setLoaded(true);
            }).catch((error) => {
                enqueueSnackbar("Error loading marksheet: " + error.message, { variant: 'warning' });
                setLoaded(true);
            });
        } catch (error) {
            enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
            setLoaded(true);
        }
    };

    const handleFilter = (event) => {
        const value = event.target.value;
        setFilterValue(value);
        if (value.trim().length > 0 && rawMarks.length > 0) {
            const filteredData = rawMarks.filter(item =>
                (item.courseName && item.courseName.toLowerCase().includes(value.toLowerCase())) ||
                (item.courseCode && item.courseCode.toLowerCase().includes(value.toLowerCase()))
            );
            setMarkDetails(filteredData);
        } else {
            setMarkDetails(rawMarks);
        }
    };

    return (
        <div className="marks-main-page animate-fade-in-up">
            <Helmet>
                <title>Marksheet & Transcripts - Vidyajyoti College of Theology</title>
                <meta name="description" content="Official Student Statement of Marks and Academic Transcript" />
                <link rel="canonical" href="/marksheet" />
            </Helmet>

            <Preloader show={!loaded} />

            {/* Breadcrumb */}
            <Breadcrumb>
                <Breadcrumb.Item>
                    <Link to="/">
                        <i className="fa fa-home" aria-hidden="true"></i> Dashboard
                    </Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>Students</Breadcrumb.Item>
                <Breadcrumb.Item active>Mark Sheet</Breadcrumb.Item>
            </Breadcrumb>

            {/* Filter and View Switcher Bar */}
            <div className="card p-3 mb-4 shadow-sm">
                <div className="d-flex justify-content-between flex-wrap align-items-center gap-3">
                    <div style={{ flex: 1, minWidth: '240px' }}>
                        <div className="position-relative">
                            <input
                                type="text"
                                className="form-control ps-5"
                                placeholder="🔍 Filter subject by name or code..."
                                value={filterValue}
                                onChange={handleFilter}
                            />
                            <i
                                className="fa fa-search position-absolute text-muted"
                                style={{ top: '50%', left: '16px', transform: 'translateY(-50%)' }}
                            ></i>
                            {filterValue && (
                                <button
                                    onClick={() => { setFilterValue(''); setMarkDetails(rawMarks); }}
                                    className="btn btn-sm position-absolute text-muted"
                                    style={{ top: '50%', right: '10px', transform: 'translateY(-50%)', border: 'none', background: 'none' }}
                                >
                                    <i className="fa fa-times"></i>
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <span className="text-muted small fw-bold d-none d-sm-inline">View Mode:</span>
                        <ButtonGroup className="p-1 rounded-3" style={{ background: 'var(--input-bg, #f8fafc)', border: '1px solid var(--card-border)' }}>
                            <Button
                                variant={viewSelected === "Print" ? "primary" : "outline-secondary"}
                                size="sm"
                                style={{ borderRadius: '8px' }}
                                onClick={() => setViewSelected("Print")}
                                title="Certificate / Transcript View"
                            >
                                <i className="fa fa-certificate me-1"></i> Certificate View
                            </Button>
                            <Button
                                variant={viewSelected === "Table" ? "primary" : "outline-secondary"}
                                size="sm"
                                style={{ borderRadius: '8px' }}
                                onClick={() => setViewSelected("Table")}
                                title="Interactive Table View"
                            >
                                <i className="fa fa-table me-1"></i> Table View
                            </Button>
                        </ButtonGroup>
                    </div>
                </div>
            </div>

            {/* View Render */}
            {loaded && searchResults.length > 0 && viewSelected === "Print" ? (
                <MarkSheetDisplay profileDetails={profileDetails} markDetails={markDetails} />
            ) : loaded && searchResults.length > 0 && viewSelected === "Table" ? (
                <MarkSheetView profileDetails={profileDetails} markDetails={markDetails} />
            ) : loaded ? (
                <div className="card p-5 text-center">
                    <i className="fa fa-folder-open-o fa-3x text-muted mb-3"></i>
                    <h5 className="fw-bold">No Examination Records Found</h5>
                    <p className="text-muted small">Please contact the Examination Office if your statement has not been uploaded yet.</p>
                </div>
            ) : null}
        </div>
    );
}
